import React, { useMemo, useEffect, useRef } from 'react';
import Filter from './component/Filter';
import { Card } from 'tea-component';
import { useLivPageTrigger } from '../../hooks/useLivPageTrigger';
import { useUserActionsSearchBox } from '../../hooks/useUserActionsSearchBox';
import { useTableConfigManager } from '../../hooks/useTableConfigManager';
import { InDevDownloadTable } from './component/InDevDownloadTable';
import { useInDevAnalytics } from '@sg/pages/StudioManagement/InDevProjects/hooks/useInDevAnalytics';
import moment from 'moment';
import LeftArrow from '@sg/assests/images/LeftArrow.svg';
import { useIntl } from 'umi';
import { createBtnPoint, SGBuriedBtnPoint, LiveTitlesTab } from '@/pages/strategic/utils/buriedPoint';
import { flattenInDevMetrics, useInDevGetColumns, IN_DEV_HEADER_GROUPS } from '@sg/pages/StudioManagement/InDevProjects/pages/InDevDownload/utils';
import './index.scss';

const InDevDownload = (props?: any) => {
  const { formatMessage } = useIntl();
  const { setDataAnalyticsPage } = props;
  const triggerCfg = useLivPageTrigger();
  const { handleOnPagingChange, pageCfg } = useUserActionsSearchBox({
    page_size: 50,
  });

  const {
    selectedTitle,
    selectedPlatforms,
    selectedRegion,
    selectedDate,
    selectStudio,
    handleYMChange: originalHandleYMChange,
  } = triggerCfg || {};

  const { selectYM } = triggerCfg;
  const isAggregate = true;

  // Wrapper function to track buried points for checkbox changes and sync isMoM state
  const handleYMChange = (newValue: string[]) => {
    const previousValue = selectYM || [];
    const wasMoMChecked = previousValue.includes('2');
    const isMoMChecked = newValue.includes('2');
    // Track MoM checkbox change and sync isMoM state
    if (wasMoMChecked !== isMoMChecked) {
      const btnPoint = createBtnPoint(LiveTitlesTab, 'mom', `MoM - ${isMoMChecked ? 'Checked' : 'Unchecked'}`);
      if (btnPoint) {
        SGBuriedBtnPoint(btnPoint);
      }
      // 同步更新 isMoM 状态
      setIsMoM(isMoMChecked);
    }
    // Call the original handler
    originalHandleYMChange(newValue);
  };

  const { sortCfg, sort, handleOnSortChange } = useTableConfigManager({
    handleOnPagingChange,
    sortColumn: [
      'spend_to_date',
      // 'total_dev_cost',
      'headcount_actual',
      // 'hc_planned',
      // 'y_copy_sales',
      // 'y_gross_revenue',
      'ytm_total_project_cost',
      'ytm_dev_cost',
      'ytm_rd_staff_cost',
      'ytm_outsourcing_cost',
      'ytm_other_rd_cost',
      'ytm_ga_overheads',
      'ytm_vgtr',
      'ytm_mkt_cost',
      'ytm_other_cost',
      'launch_time',
    ],
    sortDefaultValue: [
      {
        order: 'desc',
        by: 'spend_to_date',
      },
    ],
  });

  // 表头点击排序：sort 变化时（非首次挂载）触发数据重新拉取
  const isFirstSortRenderRef = useRef(true);
  useEffect(() => {
    if (isFirstSortRenderRef.current) {
      isFirstSortRenderRef.current = false;
      return;
    }
    handleSortChange(sort);
  }, [sort]);

  const { page: pageIndex, page_size: pageSize } = pageCfg || {};

  const fetchParams = useMemo(
    () => ({
      sort: sort?.map((task) => ({
        key: task.by,
        value: task.order,
      })),
      page: pageIndex,
      studio: selectStudio,
      page_size: pageSize,
      metrics: [
        'gross_revenue',
        'gross_base_sales',
        'gross_gaas_sales',
        'net_revenue',
        'net_base_sales',
        'net_gaas_sales',
        'unit_sales',
        'base_sales',
        'dlc_sales',
        'mau',
        'avg_dau',
        'pcu',
        'new_users',
        'returned_users',
        'retained_users',
        'payers',
        'payers_ratio',
        'arpu',
        'arppu',
      ],
      game_title: selectedTitle || [],
      country_code: selectedRegion.markets,
      start_date: moment(selectedDate[0]).format('YYYY-MM-DD'),
      end_date: moment(selectedDate[1]).format('YYYY-MM-DD'),
      include_yoy: 1,
      include_mom: 1,
      os_metrics: ['gross_revenue', 'net_revenue', 'unit_sales'],
      os: selectedPlatforms,
    }),
    [sort, pageCfg, selectedTitle, selectedRegion.markets, selectedDate, selectedPlatforms, selectStudio],
  );

  const exportFetchParams = useMemo(
    () => ({
      ...fetchParams,
      page: 0,
    }),
    [fetchParams],
  );

  const {
    loading,
    projects,
    cascadeInfoList,
    cascadeTreeOptions,
    studioProjectList,
    selectedMonth,
    setSelectedMonth,
    cascadeValue,
    setCascadeValue,
    changeFilterProjects,
    handleDateFilterChange,
    totalPa,
    isMoM,
    setIsMoM,
    handleSortChange,
  } = useInDevAnalytics();

  // 将 isMoM 布尔值转换为 CheckboxGroup 需要的数组格式
  const selectYMForCheckbox = useMemo(() => {
    return isMoM ? ['2'] : [];
  }, [isMoM]);

  const goReturnPrevious = () => setDataAnalyticsPage(false);

  const monthDate = useMemo(() => {
    return selectedMonth ? moment(selectedMonth).format('YYYY-MM') : '';
  }, [selectedMonth]);

  // 处理表格数据源
  const { records } = useMemo(() => {
    let dataSource = [];
    dataSource = flattenInDevMetrics(projects) || [];
    return {
      records: dataSource,
    };
  }, [loading, projects]);

  return (
    <div className="dbs-in-dev-download">
      <div className="dbs-live-download-header" onClick={goReturnPrevious}>
        <img src={LeftArrow} alt="" />
        <span>{formatMessage({ id: 'dbs_button_data_analytics' })}</span>
      </div>
      <div style={{ margin: '20px 0' }}>
        <Filter
          {...{
            ...triggerCfg,
            exportFetchParams,
            selectedMonth,
            setSelectedMonth,
            cascadeValue,
            setCascadeValue,
            changeFilterProjects,
            loading,
            cascadeInfoList,
            cascadeTreeOptions,
            studioProjectList,
            handleDateFilterChange,
            handleYMChange,
            isAggregate,
            totalPa,
            selectYM: selectYMForCheckbox,
            records,
            projects, // 原始项目数据，用于下载导出
          }}
        />
      </div>
      <Card>
        <Card.Body key={'indev-download'}>
          <InDevDownloadTable
            {...{
              recordKey: 'id',
              bordered: 'all',
              projects,
              triggerCfg,
              pageSize,
              pageIndex,
              handleOnPagingChange,
              className: 'dbs-studio-monthly-report-live-list',
              sortCfg,
              isLoading: loading,
              sorts: sort,
              handleOnSortChange,
              isAggregate,
              monthDate,
              total: totalPa,
              records,
              isMoM,
              tableBodyMaxH: 770, // Set max height to enable scrolling with sticky header
            }}
          />
        </Card.Body>
      </Card>
    </div>
  );
};

export default InDevDownload;
